import SubscriptionCard from "app/Homepage/PageComponents/Subscription/SubscriptionCard";
import Popup from "./Popup";
import usePayment from "_data/hooks/payment/usePayment";
import { useState } from "react";
import { AppConfig } from "AppConfig";
import { useNavigate } from "react-router-dom";
import { logEvent } from "analytics/analytics";
import {
  isBillingAddressComplete,
  isPremium,
  isSubscriptionEnding,
  isSubscriptionRenewing,
} from "__utilities/functions/subscription";
import {
  reportPaymentError,
  requiresBillingDetails,
} from "_data/services/payment/paymentErrors";

export default function SubscriptionUpgradePopup({
  hideOverlay,
  userInfo,
  refreshUser,
}) {
  const { initiatePayment, getInitialBillingAddress } = usePayment();
  const [isYearSubscription, setIsYearSubscription] = useState(false);
  const navigate = useNavigate();

  const openNewTab = () => {
    window.open(AppConfig.APP_WEBSITE_KONTAKT, "_blank");
  };

  const selectedType = () => (isYearSubscription ? "Year" : "Month");

  const goToBillingForm = () => {
    navigate("billing", {
      state: { subscriptionType: selectedType() },
    });
  };

  const subscribe = () => {
    initiatePayment(selectedType(), (success, errorCode) => {
      if (!success) {
        // Address or terms missing: the form is a better answer than an error.
        if (requiresBillingDetails(errorCode)) {
          goToBillingForm();
          return;
        }
        // Stays silent for a stale-state code -- a live subscription already exists, so the
        // refresh below corrects the UI rather than the customer being told off for it.
        reportPaymentError(errorCode);
      }
      refreshUser();
    });
  };

  /**
   * The backend refuses to create an order unless all seven billing fields are present,
   * because it renders the invoice from them. hasBillingAddress only means *an* address is
   * stored, so check the fields themselves and send the user to the form rather than letting
   * the payment attempt fail.
   */
  const startSubscription = () => {
    getInitialBillingAddress((success, billingAddress) => {
      if (
        !success ||
        !isBillingAddressComplete(billingAddress) ||
        !userInfo?.hasAcceptedTerms
      ) {
        goToBillingForm();
        return;
      }
      subscribe();
    });
  };

  const premiumButtonText = () => {
    if (isSubscriptionEnding(userInfo)) {
      return "genoptag";
    }
    return isPremium(userInfo) ? "Forny" : "køb nu";
  };

  return (
    <Popup styles={"w-[80%] h-[96%] tall:h-[90%] py-[0px]"}>
      <div className="flex flex-col w-full h-full justify-around items-center gap-y-4 py-0 relative">
        <img
          className="absolute content-darkCloseIcon top-0 right-0 m-5 cursor-pointer"
          alt=""
          onClick={() => hideOverlay()}
        />
        <p className="text-primaryBlue font-latoBold text-[20px]">
          Opgradér din løsning
        </p>
        <div className="flex flex-row w-full h-[90%] tall:h-[85%] justify-center gap-x-5 px-5 pb-2">
          <SubscriptionCard
            styles={"bg-lightBrown"}
            license={"Freemium"}
            price={"Gratis"}
            description={"Til advokater, der ønsker\n at afprøve før køb."}
            unavailableItems={[
              "50 siders begrænsning",
              "Ændringer til oprettet ekstrakt ikke muligt",
            ]}
            items={[
              "Byret",
              "Landsret",
              "Højsteret",
              "Logo",
              "Brevpapir",
              "Tidslinje",
              "Klikbart index",
              "Bogmærker",
              "Søgbar PDF-tjek",
            ]}
            onClick={() => hideOverlay()}
          />
          <SubscriptionCard
            isYearSubscription={userInfo?.subscriptionType === "Year"}
            setIsYearSubscription={setIsYearSubscription}
            styles={"bg-[#F0F3F5]"}
            license={"Premium"}
            description={
              "Til advokater, der ikke har\n behov for en entrepriseløsning."
            }
            items={[
              "Byret",
              "Landsret",
              "Højsteret",
              "Logo",
              "Brevpapir",
              "Tidslinje",
              "Klikbart index",
              "Bogmærker",
              "Søgbar PDF-tjek",
            ]}
            /* Disabled exactly when the backend would refuse: a live subscription that will
               renew by itself. One that is set to end can still be resumed through the same
               call, so the button stays live for those. */
            isButtonDisabled={isSubscriptionRenewing(userInfo)}
            buttonText={premiumButtonText()}
            onClick={() => {
              logEvent("subscription", "app_upgrade_start", "subscribe_button");
              hideOverlay();
              startSubscription();
            }}
          />
          <SubscriptionCard
            styles={"bg-[#C58B68] bg-opacity-[16%]"}
            license={"Enterprise"}
            price={"Lad os tale om det"}
            description={"Til advokater, er har brug for særlig tilpasning"}
            items={["Lokal installation", "Variabel kapacitet", "Tilpasning"]}
            buttonText={"KONTAKT FOR TILBUD"}
            onClick={() => openNewTab()}
          />
        </div>
      </div>
    </Popup>
  );
}
